import {
    Box,
    Divider,
    IconButton,
    Typography,
    Grid,
    Stack,
} from "@mui/material";
import { styled } from "@mui/material/styles";
import { ArrowBack } from "@mui/icons-material";
import { observer } from "mobx-react";
import React from "react";
import { useNavigate } from "react-router";
import { useStore } from "utils/useStore";
import ConversationsStore, {
    loadConversation,
} from "../../Stores/ConversationsStore";
import ConversationEvalCard from "./components/ConversationEvalCard";
import { durationDisplay } from "components/SoundClipEditor/Controls/Player";
import moment from "moment-timezone";
import ConversationClassifierChipList from "./components/ConversationClassifierChipList/ConversationClassifierChipList";
import AcxLoadingIndicator from "components/UI/AcxLoadingIndicator";
import { emptyGUID } from "components/Admin/Organizations/OrganizationDetail/OrganizationModules/OrganizationModuleDetails/OrganizationModuleQuestionDependencies";
import { FocusedAdditionalMetadata } from "./components/FocusedAdditionalMetadata";
import { applyTimestampHeuristic } from "components/UI/AcxDataGrid/ColumnTypes/DateColTypes";
import ContextStore from "stores/ContextStore";

const FloatContainer = styled(Grid)(({ theme }) => ({
    position: "sticky",
    top: 0,
    backgroundColor: theme.palette.white.main,
    zIndex: 10,
    maxHeight: "104px",
}));
const ContainerPaddingGrid = styled(Grid)(({ theme }) => ({
    padding: theme.spacing(2),
    paddingRight: theme.spacing(1),
}));
const LeftRightPaddingGrid = styled(Grid)(({ theme }) => ({
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(1),
}));
const PaddingTop2Box = styled(Box)(({ theme }) => ({
    paddingTop: theme.spacing(2),
}));
const GrayColorBox = styled(Box)(({ theme }) => ({
    color: "#71717A",
}));

const FocusedDrawer: React.FunctionComponent = observer(() => {
    const navigate = useNavigate();
    const store = useStore(ConversationsStore);
    const contextStore = useStore(ContextStore);

    const classifierLabelStyles = {
        fontWeight: "bold",
        paddingBottom: 1,
    };

    const closeConversation = () => {
        navigate(-1);

        // Clear any context and banner content when closing the focused conversation
        contextStore.clearBannerContent();
        contextStore.clearContext();
    };

    const nonEmptyEval =
        store.selectedConversation?.evaluationNumber !== 0 &&
        store.selectedConversation?.evaluationId !== emptyGUID;
    const formattedInteractionTimestamp = store.selectedConversation?.timestamp
        ? moment(applyTimestampHeuristic(store.selectedConversation.timestamp))
              .tz(moment.tz.guess())
              .format("MM/DD/YYYY h:mm:ss A z")
        : null;
    const canShowAdditionalMetadata =
        !store.loadConversationFailed &&
        !!store.selectedConversation &&
        store.selectedConversation.conversationId === store.selectedConversationId;

    return store.getTaskLoading(loadConversation) ? (
        <AcxLoadingIndicator size={24} alternate="PuffLoader" />
    ) : (
        <Stack style={{ width: "100%", maxWidth: "250px" }}>
            <FloatContainer item xs={12}>
                <IconButton
                    onClick={closeConversation}
                    sx={{ paddingLeft: 1.5 }}
                    size="large"
                >
                    <ArrowBack />
                </IconButton>
                <ContainerPaddingGrid item>
                    <Typography variant="h1">Details</Typography>
                </ContainerPaddingGrid>
                <Divider />
            </FloatContainer>
            <Grid container item direction="column" sx={{ paddingTop: 2 }}>
                <LeftRightPaddingGrid item>
                    <GrayColorBox fontSize={14}>Agent</GrayColorBox>
                    {store.selectedConversation?.agentFirstName
                        ? store.selectedConversation?.agentFirstName +
                          " " +
                          store.selectedConversation?.agentLastName
                        : "Unknown Agent"}
                </LeftRightPaddingGrid>
                <LeftRightPaddingGrid item>
                    {store.hierarchyLevels?.map((value) => (
                        <React.Fragment key={value.id}>
                            <PaddingTop2Box
                                fontSize={14}
                                component={GrayColorBox}
                            >
                                {value.levelName}
                            </PaddingTop2Box>
                            {value.name}
                        </React.Fragment>
                    )) ?? (
                        <>
                            <PaddingTop2Box
                                fontSize={14}
                                component={GrayColorBox}
                            >
                                Vendor
                            </PaddingTop2Box>
                            Not Available
                            <PaddingTop2Box
                                fontSize={14}
                                component={GrayColorBox}
                            >
                                Brand
                            </PaddingTop2Box>
                            Not Available
                        </>
                    )}
                </LeftRightPaddingGrid>

                {store.selectedConversation?.callDirection && (
                    <LeftRightPaddingGrid item>
                        <PaddingTop2Box fontSize={14} component={GrayColorBox}>
                            Call Direction
                        </PaddingTop2Box>
                        {store.selectedConversation?.callDirection}
                    </LeftRightPaddingGrid>
                )}

                <LeftRightPaddingGrid item>
                    {store.selectedConversation?.isChat ? (
                        <>
                            <PaddingTop2Box
                                fontSize={14}
                                component={GrayColorBox}
                            >
                                Message Count
                            </PaddingTop2Box>
                            {`${
                                store.selectedConversation.chatTranscription
                                    .length
                            } ${
                                store.selectedConversation.chatTranscription
                                    .length === 1
                                    ? "Message"
                                    : "Messages"
                            }`}
                        </>
                    ) : (
                        <>
                            <PaddingTop2Box
                                fontSize={14}
                                component={GrayColorBox}
                            >
                                Call Duration
                            </PaddingTop2Box>
                            {durationDisplay(
                                (store.selectedConversation
                                    ?.callDurationMillis ?? 0) / 1000,
                            )}
                        </>
                    )}
                </LeftRightPaddingGrid>
                {formattedInteractionTimestamp && (
                    <LeftRightPaddingGrid item>
                        <PaddingTop2Box fontSize={14} component={GrayColorBox}>
                            Interaction Date
                        </PaddingTop2Box>
                        {formattedInteractionTimestamp}
                    </LeftRightPaddingGrid>
                )}

                {canShowAdditionalMetadata && (
                    <ContainerPaddingGrid item marginTop={2}>
                        <FocusedAdditionalMetadata />
                    </ContainerPaddingGrid>
                )}
                {store.selectedConversation?.evaluationId &&
                    store.selectedConversation.evaluationNumber &&
                    nonEmptyEval && (
                        <ConversationEvalCard
                            evaluationId={
                                store.selectedConversation.evaluationId
                            }
                            evaluationNumber={
                                store.selectedConversation.evaluationNumber
                            }
                        />
                    )}
                <Divider />
                <ContainerPaddingGrid item>
                    <Typography sx={classifierLabelStyles}>
                        ML Classifiers
                    </Typography>
                    <ConversationClassifierChipList
                        conversationClassifiers={
                            store.selectedConversation?.conversationClassifiers.filter(
                                (item) =>
                                    item.classifierType === "Tensorflow" ||
                                    item.name === "ML-Topic-Cluster",
                            ) ?? []
                        }
                        showAllChips
                    />
                </ContainerPaddingGrid>
                <Divider />
                <ContainerPaddingGrid item>
                    <Typography sx={classifierLabelStyles}>
                        Classifiers
                    </Typography>
                    <ConversationClassifierChipList
                        conversationClassifiers={
                            store.selectedConversation?.conversationClassifiers.filter(
                                (item) => item.classifierType === "Lucene",
                            ) ?? []
                        }
                        showAllChips
                    />
                </ContainerPaddingGrid>
            </Grid>
        </Stack>
    );
});

export default FocusedDrawer;
